import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "./Dashboard";
import { toast } from "sonner";
import i18n from "i18next";
import { GitMerge, Cpu, Loader2, TrendingUp, Users as UsersIcon, Filter, X } from "lucide-react";
import { MatchCard } from "@/components/matches/MatchCard";
import { DiversityBar } from "@/components/matches/DiversityBar";

export default function Matches() {
    const { t } = useTranslation();
    const { workspace } = useAuth();
    const [matches, setMatches] = useState([]);
    const [filter, setFilter] = useState("");
    const [companyFilter, setCompanyFilter] = useState("");
    const [candidateFilter, setCandidateFilter] = useState("");
    const [poolFilter, setPoolFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [rebuilding, setRebuilding] = useState(false);
    const [digest, setDigest] = useState(null);
    const [rescoring, setRescoring] = useState(false);
    // Multi-pool support — welke pool gebruiken we bij rebuild?
    const [pools, setPools] = useState([]);
    const [rebuildPool, setRebuildPool] = useState("all");
    // Sprint 4b · #15 Diversity-lens state
    const diversityLensAvailable = !!workspace?.diversity_lens?.enabled;
    const [diversityActive, setDiversityActive] = useState(false);
    const [divBusy, setDivBusy] = useState(false);
    const [divPreview, setDivPreview] = useState(null);   // {summary, matches[]}

    useEffect(() => {
        api.get("/matching/rescore/last-digest").then((r) => {
            if (r.data?.available) setDigest(r.data.digest);
        }).catch(() => {});
        api.get("/talent-pool/pools").then((r) => setPools(r.data || [])).catch(() => {});
    }, []);

    const runRescore = async () => {
        setRescoring(true);
        try {
            const r = await api.post("/matching/rescore/run");
            setDigest(r.data);
            toast.success(t("matches.rescore_done", { improved: r.data.improved, declined: r.data.declined }));
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || t("matches.rescore_failed")); }
        finally { setRescoring(false); }
    };

    const load = () => {
        const params = filter ? `?status=${filter}` : "";
        api.get(`/matches${params}`).then((r) => { setMatches(r.data); setLoading(false); });
    };

    useEffect(() => { load(); }, [filter]);

    const act = async (id, action) => {
        // Anti-hallucinatie beleid (2b): kandidaat automatisch verifiëren vóór
        // outreach start; alleen waarschuwen bij lage trust (geen blokkade).
        if (action === "start_outreach") {
            const m = matches.find((x) => x.id === id);
            let ver = m?.candidate?.verification;
            if (!ver && m?.candidate_id) {
                toast.info(t("verify.auto_running"));
                try { ver = (await api.post(`/verify/candidate/${m.candidate_id}`)).data; }
                catch { ver = null; }
            }
            if (ver && ver.trust_score < 50 && !confirm(t("verify.low_trust_candidate_confirm", { score: ver.trust_score }))) {
                load();
                return;
            }
        }
        try {
            await api.post(`/matches/${id}/action`, { action });
            toast.success(action === "approve" ? t("matches.toast_approved") : action === "reject" ? t("matches.toast_rejected") : t("matches.toast_outreach"));
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("matches.action_failed"));
        }
    };

    const rebuild = async () => {
        setRebuilding(true);
        try {
            const body = { top_k: 3 };
            if (rebuildPool !== "all") body.pools = [rebuildPool];
            const r = await api.post("/matching/rebuild", body);
            toast.success(t("matches.rebuild_done", { n: r.data.new_matches }));
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || t("matches.rebuild_failed")); }
        finally { setRebuilding(false); }
    };

    // --- Sprint 4b · #15 Diversity-lens ---
    const toggleDiversity = async () => {
        if (diversityActive) {
            setDiversityActive(false);
            setDivPreview(null);
            return;
        }
        setDivBusy(true);
        try {
            const dl = workspace?.diversity_lens || {};
            const r = await api.post("/matching/diversity/preview", {
                boost_underrepresented_gender: dl.boost_underrepresented_gender !== false,
                balance_seniority: dl.balance_seniority !== false,
                balance_regions: !!dl.balance_regions,
                persist: false,
            });
            setDivPreview(r.data);
            setDiversityActive(true);
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("matches.diversity_failed")); }
        finally { setDivBusy(false); }
    };

    // --- Company / Candidate filter opties (afgeleid uit geladen matches) ---
    const companyOptions = useMemo(
        () => [...new Set(matches.map((m) => m.company_name).filter(Boolean))].sort(),
        [matches]);
    const candidateOptions = useMemo(
        () => [...new Set(matches.map((m) => m.candidate_name).filter(Boolean))].sort(),
        [matches]);

    const visibleMatches = useMemo(() => matches.filter((m) => {
        if (companyFilter && m.company_name !== companyFilter) return false;
        if (candidateFilter && m.candidate_name !== candidateFilter) return false;
        if (poolFilter && (m.talent_pool || "external") !== poolFilter) return false;
        return true;
    }), [matches, companyFilter, candidateFilter, poolFilter]);

    const hasEntityFilter = !!(companyFilter || candidateFilter || poolFilter);
    // key → display naam voor de pool-badge op elke match-kaart
    const poolMap = useMemo(() => Object.fromEntries(pools.map((p) => [p.key, p.name])), [pools]);

    return (
        <PageHeader
            title={t("matches.title")}
            subtitle={t("matches.subtitle")}
            actions={
                <div className="flex gap-2 items-center flex-wrap">
                    {diversityLensAvailable && (
                        <button onClick={toggleDiversity} disabled={divBusy} data-testid="diversity-toggle"
                            className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-mono uppercase tracking-[0.16em] transition-colors ${diversityActive ? "bg-[#0891b2] text-white hover:bg-[#0e7490]" : "border border-[#0891b2]/40 text-[#0891b2] hover:bg-[#0891b2]/10"} disabled:opacity-60`}>
                            {divBusy ? <Loader2 className="animate-spin" size={12}/> : <UsersIcon size={12}/>}
                            {diversityActive ? t("matches.diversity_on") : t("matches.diversity_lens")}
                        </button>
                    )}
                    <button onClick={runRescore} disabled={rescoring} data-testid="matches-rescore"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#a855f7] text-white text-xs font-mono uppercase tracking-[0.16em] hover:bg-[#a855f7]/85 disabled:opacity-60">
                        {rescoring ? <Loader2 className="animate-spin" size={12}/> : <TrendingUp size={12}/>}
                        {rescoring ? t("matches.rescoring") : t("matches.rescore_now")}
                    </button>
                    {/* Pool-selectie voor rebuild (multi-pool support) */}
                    <select value={rebuildPool} onChange={(e) => setRebuildPool(e.target.value)}
                        data-testid="match-pool-select"
                        title={t("matches.pool_match_label")}
                        className="bg-[var(--jr-surface-2)] border border-white/10 rounded-full px-3 py-2 text-xs text-[var(--jr-text-2)] font-mono uppercase tracking-[0.12em] focus:border-[var(--jr-brand)] outline-none cursor-pointer">
                        <option value="all">{t("matches.pool_all")}</option>
                        {pools.map((p) => (
                            <option key={p.key} value={p.key}>{`${p.name} (${p.candidate_count})`}</option>
                        ))}
                    </select>
                    <button onClick={rebuild} disabled={rebuilding} data-testid="matches-rebuild"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[var(--jr-brand)] text-white text-xs font-mono uppercase tracking-[0.16em] hover:bg-[var(--jr-brand-2)] disabled:opacity-60">
                        {rebuilding ? <Loader2 className="animate-spin" size={12}/> : <Cpu size={12}/>}
                        {rebuilding ? t("matches.rebuilding") : t("matches.rebuild")}
                    </button>
                    {["", "proposed", "approved", "outreach_started"].map((f) => (
                        <button key={f || "all"} onClick={() => setFilter(f)} data-testid={`match-filter-${f || "all"}`}
                            className={`text-xs font-mono uppercase tracking-[0.16em] px-3 py-2 rounded-full transition-colors ${filter === f ? "bg-[var(--jr-brand)] text-white" : "border border-white/10 text-[var(--jr-text-2)] hover:bg-white/5"}`}>
                            {f === "" ? t("common.all") : t(`matches.status.${f}`)}
                        </button>
                    ))}
                </div>
            }
        >
            {/* Bedrijf + kandidaat filterbalk */}
            <div className="mb-4 flex items-center gap-2 flex-wrap" data-testid="match-entity-filters">
                <Filter size={12} className="text-[var(--jr-text-3)]"/>
                <select value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)}
                    data-testid="match-filter-company"
                    className="bg-[var(--jr-surface-2)] border border-white/10 rounded-full px-3 py-1.5 text-xs text-[var(--jr-text-2)] focus:border-[var(--jr-brand)] outline-none cursor-pointer max-w-[220px]">
                    <option value="">{t("matches.all_companies")}</option>
                    {companyOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={candidateFilter} onChange={(e) => setCandidateFilter(e.target.value)}
                    data-testid="match-filter-candidate"
                    className="bg-[var(--jr-surface-2)] border border-white/10 rounded-full px-3 py-1.5 text-xs text-[var(--jr-text-2)] focus:border-[var(--jr-amber)] outline-none cursor-pointer max-w-[220px]">
                    <option value="">{t("matches.all_candidates")}</option>
                    {candidateOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                {/* Pool-filter — "Cronos-first": 1 klik om alleen interne matches te zien */}
                <select value={poolFilter} onChange={(e) => setPoolFilter(e.target.value)}
                    data-testid="match-filter-pool"
                    className={`border rounded-full px-3 py-1.5 text-xs focus:border-[var(--jr-amber)] outline-none cursor-pointer max-w-[200px] ${poolFilter === "cronos" ? "bg-[var(--jr-amber)]/12 border-[var(--jr-amber)]/40 text-[var(--jr-amber)]" : "bg-[var(--jr-surface-2)] border-white/10 text-[var(--jr-text-2)]"}`}>
                    <option value="">{t("matches.pool_all")}</option>
                    {pools.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
                </select>
                {hasEntityFilter && (
                    <button onClick={() => { setCompanyFilter(""); setCandidateFilter(""); setPoolFilter(""); }}
                        data-testid="match-filter-clear"
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full border border-white/10 text-[var(--jr-text-3)] hover:text-white hover:bg-white/5 transition-colors">
                        <X size={11}/> {t("matches.clear_filters")}
                    </button>
                )}
                <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] ml-auto" data-testid="match-count">
                    {t("matches.n_shown", { n: visibleMatches.length, total: matches.length })}
                </span>
            </div>

            {/* Sprint 4b · #15 Diversity summary panel */}
            {diversityActive && divPreview?.summary && (
                <div className="mb-4 rounded-xl border border-[#0891b2]/30 bg-[#0891b2]/[0.05] p-4" data-testid="diversity-panel">
                    <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                        <div className="flex items-center gap-2">
                            <UsersIcon size={14} className="text-[#0891b2]"/>
                            <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#0891b2]">{t("matches.diversity_lens")} · {divPreview.summary.total} matches</span>
                        </div>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3">
                        <DiversityBar label={t("matches.div_gender")} data={divPreview.summary.gender}/>
                        <DiversityBar label={t("matches.div_seniority")} data={divPreview.summary.seniority}/>
                        <DiversityBar label={t("matches.div_region")} data={divPreview.summary.region}/>
                    </div>
                </div>
            )}
            {/* Nachtelijke re-score digest widget (Feature #12) */}
            {digest && digest.scanned > 0 && (
                <div className="mb-4 rounded-xl border border-[#a855f7]/30 bg-gradient-to-br from-[#a855f7]/[0.06] to-transparent p-4" data-testid="rescore-digest">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#a855f7]">{t("matches.digest_title")}</div>
                            <div className="text-sm text-white mt-1">
                                {t("matches.digest_scanned", { n: digest.scanned })} · <span className="text-[#22c55e]">↑ {digest.improved} {t("matches.digest_better")}</span> · <span className="text-[#ef4444]">↓ {digest.declined} {t("matches.digest_worse")}</span> · <span className="text-[var(--jr-text-3)]">{digest.unchanged} {t("matches.digest_same")}</span>
                            </div>
                        </div>
                    </div>
                    {(digest.biggest_up?.length || 0) > 0 && (
                        <div className="mt-3 grid md:grid-cols-2 gap-2">
                            {digest.biggest_up.filter(u => u.delta > 0).slice(0, 4).map((u) => (
                                <div key={u.match_id} className="text-xs rounded-lg bg-[#22c55e]/[0.06] border border-[#22c55e]/25 p-2">
                                    <div className="flex items-center justify-between">
                                        <span className="text-white truncate">{u.candidate} → {u.company}</span>
                                        <span className="text-[#22c55e] font-mono">+{u.delta}</span>
                                    </div>
                                    <div className="text-[10px] text-[var(--jr-text-3)]">{u.prev}% → {u.new}%</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
            {loading ? (
                <div className="text-[var(--jr-text-2)]">{t("common.loading")}</div>
            ) : visibleMatches.length === 0 ? (
                <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-10 text-center">
                    <GitMerge className="text-[var(--jr-amber)] mx-auto" size={28}/>
                    <p className="mt-4 text-[var(--jr-text-2)]">{t("matches.empty_filter")}</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {(() => {
                        // Merge diversity data + resort when active
                        let list = visibleMatches;
                        if (diversityActive && divPreview?.matches) {
                            const boostById = {};
                            divPreview.matches.forEach((d) => { boostById[d.id] = d; });
                            list = visibleMatches.map((m) => ({ ...m, __div: boostById[m.id] }));
                            list.sort((a, b) => (b.__div?.diversity_adjusted_confidence || b.confidence) - (a.__div?.diversity_adjusted_confidence || a.confidence));
                        }
                        return list.map((m) => <MatchCard key={m.id} m={m} onAction={act} diversityActive={diversityActive} poolMap={poolMap}/>);
                    })()}
                </div>
            )}
        </PageHeader>
    );
}
